<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { isMobile } from "../../stores";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        SearchInput,
        PageLoading,
        SvelteTable,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import {
        getActivityLogsActionType,
        getFormatDateAndTime,
        getText,
        replaceURL,
        getDisplayUserName,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getDaysDifferFrom,
        viewLog,
        getTodayWithTime,
        getFormatDateAndTimeWithSeconds,
    } from "../../Utils/utilFunctions.svelte";

    import { user } from "../../stores";

    import ChangeLogsFormatted from "../PatientEncounter/components/ChangeLogsFormatted.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { DefaultLogsTraceBackDays } from "../../Utils/utilConstants.svelte";
    import { LogTypes } from "../../Utils/utilEnums.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isLoadingActivityLogs = false,
        returnedActivityLogsArr = [],
        filters = {};

    let page = 0,
        pageSize = 25,
        count;

    let selectedDoctors = [];

    let showFilters = false;

    $: isUserEnableSeeAllFilters = $user.role >= 20;

    const columns = [
        {
            key: "date",
            title: "Date",
            headerClass: "text-left pl-3 cursor-default rounded-tl-xl w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getFormatDateAndTimeWithSeconds(v.createdDate),
        },
        {
            key: "user",
            title: "User",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getDisplayUserName(v),
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getText(v.accountName),
        },
        {
            key: "controller",
            title: "Controller",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getText(v.controller),
        },
        {
            key: "actionType",
            title: "Action Type",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getActivityLogsActionType(v.actionType),
        },
        {
            key: "entityName",
            title: "Action",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getText(v.entityName, "-"),
        },
        {
            key: "entityId",
            title: "ID",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getText(v.entityId, "-"),
        },
        {
            key: "source",
            title: "Source",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            value: (v) => getText(v.source),
        },
        {
            key: "changes",
            title: "Changes",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: "px-3 cursor-default min-w-[420px]",
            renderComponent: {
                component: ChangeLogsFormatted,
                props: {
                    fromTable: true,
                },
            },
            csvContent: (v) => `${v.changes}`,
        },
    ];

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        if (!filters.dateFrom) {
            filters.dateFrom = getDaysDifferFrom(
                -1 * DefaultLogsTraceBackDays,
                new Date(),
            );
            updateURL();
        }

        loadActivityLogs(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];

                if (arr[i].includes("entityId="))
                    filters.entityId = Number(arr[i].split("=")[1]);

                if (arr[i].includes("controller="))
                    filters.controller = arr[i].split("=")[1];

                if (arr[i].includes("fromDate="))
                    filters.dateFrom = arr[i].split("=")[1];

                if (arr[i].includes("toDate="))
                    filters.dateTo = arr[i].split("=")[1];

                if (isUserEnableSeeAllFilters) {
                    if (arr[i].includes("doctorId=")) {
                        if (isNaN(arr[i].split("=")[1])) {
                            filters.doctorId = arr[i].split("=")[1];
                            selectedDoctors = filters.doctorId
                                .split(",")
                                .map((id) => Number(id));
                        } else {
                            filters.doctorId = Number(arr[i].split("=")[1]);
                            selectedDoctors = [Number(filters.doctorId)];
                        }
                    }
                }
            }

            updateURL();
        }
    };

    const loadActivityLogs = async (start, limit, params) => {
        isLoadingActivityLogs = true;

        await apiMethods.getMethod
            .getActivityLogs(start, limit, params)
            .then((res) => _handleActivityLogs(res));

        isLoadingActivityLogs = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadActivityLogs(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/ActivityLogs/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadActivityLogs(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadActivityLogs(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        viewLog(LogTypes.Activity, rowData.id);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const toggleFilters = () => (showFilters = !showFilters);

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    // HELPER FUNCTIONS
    const _handleActivityLogs = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnedActivityLogsArr = [...res.results];

            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        if (filters.entityId) params["filter[entityId]"] = filters.entityId;

        if (filters.controller)
            params["filter[controller]"] = filters.controller;

        if (filters.dateFrom) params["filter[dateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[dateTo]"] = filters.dateTo;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[entityId]"])
                str += `&entityId=${params["filter[entityId]"]}`;

            if (params["filter[controller]"])
                str += `&controller=${params["filter[controller]"]}`;

            if (params["filter[dateFrom]"])
                str += `&fromDate=${params["filter[dateFrom]"]}`;

            if (params["filter[dateTo]"])
                str += `&toDate=${params["filter[dateTo]"]}`;

            if (isUserEnableSeeAllFilters) {
                if (params["filter[doctorId]"])
                    str += `&doctorId=${params["filter[doctorId]"]}`;
            }
        }

        return encodeSearchParams(str);
    };
</script>

<!-- <p class="text-primary text-xl pt-12 pl-6">Activity Logs page coming soon</p> -->
<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}

    {#if showFilters || !$isMobile}
        <div
            class="flex flex-wrap flex-row gap-4 items-end"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isDisable={isLoadingActivityLogs}
            />

            <SearchInput
                bind:value={filters.entityId}
                title="Entity Id"
                inputFunc={handleFilterSelect}
                isDisable={isLoadingActivityLogs}
            />

            {#if isUserEnableSeeAllFilters}
                <DefaultDoctorFilter
                    bind:filter={filters}
                    filterPropertyName={"doctorId"}
                    onChangeFunc={() => {
                        selectedDoctors = filters["doctorId"].split(",");
                        handleFilterSelect();
                    }}
                />

                <DoctorMultiSearchSelect
                    bind:searchResult={selectedDoctors}
                    onChangeFunc={handleSelectDoctor}
                    inputClass="w-32"
                    zIndex="3"
                />
            {/if}

            <SearchInput
                bind:value={filters.controller}
                title="Controller"
                inputFunc={handleFilterSelect}
                isDisable={isLoadingActivityLogs}
            />

            {#if !$user.settings?.disableDateRangeInput}
                <SearchDateRangeInput
                    bind:startDate={filters.dateFrom}
                    bind:endDate={filters.dateTo}
                    title="Date Range"
                    inputClass="borderB w-[272px]"
                    on:change={handleFilterSelect}
                    showClearButton
                    disable={isLoadingActivityLogs}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />
            {:else}
                <SearchInput
                    bind:value={filters.dateFrom}
                    title="Date from"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingActivityLogs}
                    maxDate={filters.dateTo ?? undefined}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />

                <SearchInput
                    bind:value={filters.dateTo}
                    title="Date to"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingActivityLogs}
                    maxDate={filters.dateFrom ?? undefined}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />
            {/if}

            <ResetFilterLink
                bind:filter={filters}
                onResetFilter={clearFilterDoctor}
            />
        </div>
    {/if}

    <div class="relative flex-1">
        {#if isLoadingActivityLogs}
            <PageLoading />
        {:else}
            <div class="overflow-y-auto absolute inset-0">
                <SvelteTable
                    {columns}
                    rows={returnedActivityLogsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnedActivityLogsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnedActivityLogsArr,
            fileName: "Activity Logs",
            allDataApi: async () =>
                await apiMethods.getMethod.getActivityLogs(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
